import React from "react";
import { createRoot } from "react-dom/client";
import { cn } from "../utils/cn.js";
import ImageZoom from "./ImageZoom.js";
import { shortenUrl } from "../utils/index.js";

const shortenHtml = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const processNode = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      // Replace img tags with a div
      if (element.tagName === "IMG") {
        // const div = document.createElement("div");
        element.classList.add("img-react-root");
        // element.parentNode?.replaceChild(div, element);
      } else if (
        element.tagName === "A" &&
        element instanceof HTMLAnchorElement
      ) {
        const fullUrl = element.href;
        element.textContent = shortenUrl(fullUrl);
      }
      Array.from(element.childNodes).forEach(processNode);
    } else if (node.nodeType === Node.TEXT_NODE && node.textContent) {
      const fragment = document.createDocumentFragment();
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      let lastIndex = 0;
      let match;

      while ((match = urlRegex.exec(node.textContent)) !== null) {
        const url = match[0];
        const shortened = shortenUrl(url);

        // Add text before the URL
        fragment.appendChild(
          document.createTextNode(
            node.textContent.slice(lastIndex, match.index)
          )
        );

        // Create and add the link
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank"; // Add target="_blank"
        link.textContent = shortened;
        fragment.appendChild(link);

        lastIndex = urlRegex.lastIndex;
      }

      // Add any remaining text
      if (lastIndex < node.textContent.length) {
        fragment.appendChild(
          document.createTextNode(node.textContent.slice(lastIndex))
        );
      }

      node.parentNode?.replaceChild(fragment, node);
    }
  };

  processNode(doc.body);
  return doc.body.innerHTML;
};

const useMergeRefs = <T extends HTMLElement>(
  refs: Array<React.MutableRefObject<T> | React.LegacyRef<T>>
) => {
  return React.useCallback(
    (element: T) => {
      refs.forEach((ref) => {
        if (typeof ref === "function") {
          ref(element);
        } else if (ref != null) {
          (ref as React.MutableRefObject<T | null>).current = element;
        }
      });
    },
    [refs]
  );
};

const RichText = React.forwardRef<
  HTMLDivElement,
  { html: string; className?: string }
>(({ html, className }, ref) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const rootsMapRef = React.useRef(new Map<HTMLElement, any>());
  const mergedRef = useMergeRefs([containerRef, ref]);

  React.useEffect(() => {
    const currentRoots = new Map(rootsMapRef.current);

    if (containerRef.current) {
      const reactRoots = containerRef.current.querySelectorAll(
        "img"
      ) as NodeListOf<HTMLElement>;

      reactRoots.forEach((root) => {
        let reactRoot = rootsMapRef.current.get(root);
        if (!reactRoot && root.parentElement) {
          reactRoot = createRoot(root.parentElement);
          rootsMapRef.current.set(root, reactRoot);
        }

        if (reactRoot) {
          const imgProps = {
            src: root.getAttribute("src") || "",
            alt: root.getAttribute("alt") || "",
            width: root.getAttribute("width") || undefined,
            height: root.getAttribute("height") || undefined,
            className: root.getAttribute("class") || undefined
          };
          reactRoot.render(
            <ImageZoom src={imgProps.src}>
              <img {...imgProps} />
            </ImageZoom>
          );
        }
      });
    }

    return () => {
      // Unmount only the roots that are no longer needed
      currentRoots.forEach((root, element) => {
        if (!rootsMapRef.current.has(element)) {
          root.unmount();
        }
      });
    };
  }, [html]);
  const proseDefault = className?.toLowerCase().includes("prose")
    ? className
    : "prose prose-slate dark:prose-invert";
  return (
    <div
      ref={mergedRef}
      dangerouslySetInnerHTML={{ __html: shortenHtml(html) }}
      className={cn(`${proseDefault} break-words`, className)}
    />
  );
});

export { RichText };
